import * as hiveCore from '@gitbook/hive-core';

type ActiveDiffPageInput = {
    page: hiveCore.RevisionTreePage;
    currentRevision: hiveCore.Revision;
    baseRevision: hiveCore.Revision;
};

type FirstActiveDiffPageInput = {
    pages: hiveCore.RevisionTreePage[];
    currentRevision: hiveCore.Revision;
    baseRevision: hiveCore.Revision;
};

type ClosestActiveDiffPageInput = FirstActiveDiffPageInput & {
    previousActivePageIds: string[];
    currentPageId: string | undefined;
};

/**
 * Return whether a tree page has its own visible page-level diff.
 */
export function isActiveDiffPage(input: ActiveDiffPageInput): boolean {
    const { page, currentRevision, baseRevision } = input;

    if (!page.diff || page.diff === hiveCore.PageDiffStatus.Deleted) {
        return false;
    }

    if (page.page.type !== hiveCore.RevisionPageType.Document) {
        return false;
    }

    if (page.diff === hiveCore.PageDiffStatus.Added) {
        return true;
    }

    const currentPage = currentRevision.pages[page.page.id];
    const basePage = baseRevision.pages[page.page.id];

    if (!currentPage || !basePage) {
        return true;
    }

    return hiveCore.comparePages(basePage, currentPage, { compareMetadata: false });
}

/**
 * Find all pages that have a visible diff in the current revision, in tree order.
 */
export function findActiveDiffPages(
    input: FirstActiveDiffPageInput
): Array<hiveCore.RevisionTreePage<hiveCore.RevisionPage>> {
    const { pages, currentRevision, baseRevision } = input;
    const result: Array<hiveCore.RevisionTreePage<hiveCore.RevisionPage>> = [];

    const visit = (treePages: hiveCore.RevisionTreePage[]) => {
        for (const page of treePages) {
            if (isActiveDiffPage({ page, currentRevision, baseRevision })) {
                result.push(page);
            }

            visit(page.pages);
        }
    };

    visit(pages);
    return result;
}

/**
 * Find the closest active diff page after the current page disappeared from the active diff list.
 */
export function findClosestActiveDiffPage(
    input: ClosestActiveDiffPageInput
): hiveCore.RevisionTreePage<hiveCore.RevisionPage> | undefined {
    const { previousActivePageIds, currentPageId } = input;
    const activePages = findActiveDiffPages(input);

    // The current page may no longer be in the changed list (its change was reverted, or the page
    // was removed entirely). Use the pre-revert order to find where it sat, then pick the nearest
    // page that's *still* changed — the previous one if any, otherwise the next.
    const previousIndex = currentPageId ? previousActivePageIds.indexOf(currentPageId) : -1;
    if (previousIndex === -1) {
        return activePages[0];
    }

    const activePageIds = new Set(activePages.map((page) => page.page.id));
    const isStillActive = (pageId: string) => activePageIds.has(pageId);
    const targetId =
        previousActivePageIds.slice(0, previousIndex).findLast(isStillActive) ??
        previousActivePageIds.slice(previousIndex + 1).find(isStillActive);

    return activePages.find((page) => page.page.id === targetId);
}
